import { router } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Platform, View } from "react-native";
import { Hide, mq } from "react-native-unistyles";

import Button from "$src/core/components/atoms/Button/Button";
import Pressable from "$src/core/components/atoms/Pressable";
import ScrollView from "$src/core/components/atoms/ScrollView/ScrollView";
import Card from "$src/core/components/molecules/Card/Card";
import useTranslation from "$src/core/hooks/use-translation";

import $, { IMAGE_DIMENSIONS } from "./SwimmingLane.web.styles";
import SwimmingLanePreviousOverlay from "./SwimmingLanePreviousOverlay";
import type { SwimmingLaneProps } from "./types";

/** How many cards one press of the previous/next chevron moves the lane. */
const ITEMS_PER_SCROLL = 2;

const SwimmingLane = ({
  ref,
  type = "landscape",
  items,
  style,
  contentContainerStyle,
  hidePreviousOverlay = false,
  onScrollAbilitiesChange,
}: SwimmingLaneProps) => {
  const { t } = useTranslation();

  const listId = useId();

  const itemRefs = useRef<View[]>([]);

  const [canScrollNext, setCanScrollNext] = useState(true);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  $.useVariants({ type, canScrollNext });

  const scrollByItems = useCallback(
    (count: number) => {
      const list = document.getElementById(listId);
      const [first, second] = itemRefs.current as unknown as (
        | HTMLElement
        | undefined
      )[];

      if (!list || !first) {
        return;
      }

      const pitch = second
        ? second.offsetLeft - first.offsetLeft
        : first.offsetWidth;

      list.scrollBy({ left: pitch * count, behavior: "smooth" });
    },
    [listId],
  );

  const handleMovePrevious = useCallback(() => {
    scrollByItems(-ITEMS_PER_SCROLL);
  }, [scrollByItems]);

  const handleMoveNext = () => {
    scrollByItems(ITEMS_PER_SCROLL);
  };

  const updateScrollAbilities = useCallback(() => {
    const list = document.getElementById(listId);
    if (list) {
      setCanScrollPrev(list.scrollLeft > 0);
      setCanScrollNext(list.scrollLeft < list.scrollWidth - list.clientWidth);
    }
  }, [listId]);

  useLayoutEffect(() => {
    const list = document.getElementById(listId);
    if (list) {
      list.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    updateScrollAbilities();
  }, [listId, updateScrollAbilities]);

  useEffect(() => {
    onScrollAbilitiesChange?.({
      canScrollPrev,
      canScrollNext,
      scrollPrevious: handleMovePrevious,
    });
  }, [
    canScrollPrev,
    canScrollNext,
    handleMovePrevious,
    onScrollAbilitiesChange,
  ]);

  useImperativeHandle(ref, () => ({
    requestFocusOnIndex: (index: number) => {
      itemRefs.current.at(index)?.focus();
    },
  }));

  return (
    <View>
      {!hidePreviousOverlay && (
        <SwimmingLanePreviousOverlay
          visible={canScrollPrev}
          onPress={handleMovePrevious}
        />
      )}

      <ScrollView
        id={listId}
        horizontal
        accessibilityRole={Platform.OS === "web" ? undefined : "list"}
        style={style}
        contentContainerStyle={contentContainerStyle}
        onScroll={updateScrollAbilities}
        showsHorizontalScrollIndicator={false}
      >
        {items.map((item, index) => (
          <Pressable
            key={item.id}
            ref={(el: View | null) => {
              if (el) {
                itemRefs.current[index] = el;
              }
            }}
            accessibilityRole="button"
            style={$.pressable}
            onPress={() => router.push(`/${item.type}/${item.id}`)}
          >
            <Card
              type={type}
              title={item.title}
              subtitle={item.subtitle}
              image={item.image ?? item.images}
              duration={item.duration}
              progress={item.progress}
              cardStyle={$.card}
              imageDimensions={IMAGE_DIMENSIONS.card[type]}
            />
          </Pressable>
        ))}
      </ScrollView>

      <Hide mq={mq.only.width(undefined, "lg")}>
        <View style={[$.overlay, $.nextOverlay]}>
          <Button
            name="swimming_lane_next"
            accessibilityLabel={t("COMMON/NEXT/CTA/LABEL")}
            preset="secondary"
            icon="chevron-right"
            onPress={handleMoveNext}
          />
        </View>
      </Hide>
    </View>
  );
};

export default SwimmingLane;
